// Parameters settings in the keeper's units. Targets and ranges are stored
// in °F and ppt; a Celsius keeper saw 78 / 76 / 80 with a small "°F" label,
// typed 24 / 26 as Celsius, and those were saved as °F, so a normal 25.6 °C
// alerted HIGH (newkeeper audit 2026-10-01). Salinity showed in ppt to an
// S.G. keeper.

import { getDisplayUnits, fToC, cToF } from './units.js';
import { formatSalinity, salinityToPpt, salinityUnitLabel } from './salinity.js';
import { implausibleReading } from '../../shared/reading-plausibility.js';

// The unit label beside a row, in the keeper's units.
export function settingsUnitLabel(key, storedUnit, { tank, salinityUnit } = {}) {
  if (key === 'temp') return getDisplayUnits(tank).temp === 'C' ? '°C' : '°F';
  if (key === 'salinity') return salinityUnitLabel(salinityUnit);
  return storedUnit || '';
}

// A stored number as the form shows it.
export function toFormValue(key, stored, { tank, salinityUnit } = {}) {
  if (stored == null || stored === '' || !Number.isFinite(Number(stored))) return '';
  const n = Number(stored);
  if (key === 'temp' && getDisplayUnits(tank).temp === 'C') {
    return String(Math.round(fToC(n) * 10) / 10);
  }
  if (key === 'salinity' && salinityUnit === 'sg') {
    // Four places: a 34.7-35.3 ppt band would read 1.026-1.026 at three.
    return String(formatSalinity(n, 'sg', 4));
  }
  return String(n);
}

// What the keeper typed, back to the stored unit, or null.
export function fromFormValue(key, text, { tank } = {}) {
  if (text == null) return null;
  const s = String(text).trim().replace(',', '.');
  if (s === '') return null;
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  if (key === 'temp' && getDisplayUnits(tank).temp === 'C') {
    return Math.round(cToF(n) * 100) / 100;
  }
  // Detection-first, like a reading: S.G. lives below 2.
  if (key === 'salinity') return salinityToPpt(n);
  return n;
}

// What to save for one field of a row: the stored number itself when the
// keeper left the field as it was shown. Converting the shown text back
// moved an untouched 76 °F low limit to 75.92 (shown 24.4 °C) whenever
// another field of the row was saved, and a band "change" drops its cited
// source on the server.
export function formFieldToStored(key, text, stored, ctx = {}) {
  const shown = toFormValue(key, stored, ctx);
  if (shown !== '' && String(text ?? '').trim() === shown) return Number(stored);
  return fromFormValue(key, text, ctx);
}

// A stored temperature or salinity range no tank can hold, found before
// the Settings form converted units (2026-10-01): a Celsius keeper's 24-26
// was saved as °F and now shows as -4.4 to -3.3 °C, and an S.G. typed into
// a ppt box was saved as 1.025 ppt. Nothing here rewrites the stored band:
// the warning says so and `fix` fills the boxes with the numbers read the
// way they were meant, for the keeper to check and Save.
const BAND_FIELDS = [
  ['target', (r) => r?.target],
  ['low', (r) => r?.acceptable?.[0]],
  ['high', (r) => r?.acceptable?.[1]],
];

export function storedBandWarning(key, row, ctx = {}) {
  if (key !== 'temp' && key !== 'salinity') return null;
  const subtypeSlug = ctx.tank?.subtype_slug || null;
  const bad = [];
  for (const [field, get] of BAND_FIELDS) {
    const v = get(row);
    if (v == null || v === '' || !Number.isFinite(Number(v))) continue;
    if (implausibleReading(key, Number(v), { subtypeSlug })) bad.push([field, Number(v)]);
  }
  if (!bad.length) return null;
  if (key === 'temp') {
    // Read as °C, each number must land in the range a tank can hold.
    const asF = bad.map(([f, v]) => [f, Math.round(cToF(v) * 100) / 100]);
    if (asF.every(([, f]) => !implausibleReading('temp', f))) {
      return {
        message:
          'This range looks like °C numbers saved as °F, so it is wrong for this tank. Tap "Read as °C" to fill the boxes, check them, then Save.',
        fixLabel: 'Read as °C',
        fix: Object.fromEntries(asF.map(([f, v]) => [f, toFormValue('temp', v, ctx)])),
      };
    }
  } else {
    const asPpt = bad.map(([f, v]) => [f, salinityToPpt(v)]);
    if (
      bad.every(([, v]) => v >= 1 && v < 1.05) &&
      asPpt.every(([, p]) => !implausibleReading('salinity', p, { subtypeSlug }))
    ) {
      return {
        message:
          'This range looks like S.G. numbers saved as ppt, so it is wrong for this tank. Tap "Read as S.G." to fill the boxes, check them, then Save.',
        fixLabel: 'Read as S.G.',
        fix: Object.fromEntries(asPpt.map(([f, v]) => [f, toFormValue('salinity', v, ctx)])),
      };
    }
  }
  return {
    message: 'This range is not one a tank can hold. Check the numbers and the unit, then Save.',
    fixLabel: null,
    fix: null,
  };
}
